import { useEffect, useRef } from "react";
import { trackPageView } from "./analytics";

/**
 * Sets the document title and the handful of meta tags each page overrides.
 *
 * This replaces `react-helmet-async`, which is installed at 2.0.5 and renders
 * nothing at all under React 18's StrictMode — the double-invoked effect tears
 * its dispatcher down and never puts it back, so every route kept the title
 * from `index.html`. Rather than pin an unmaintained library to a working
 * version, the job is small enough to do directly.
 *
 * Worth knowing what this can and cannot buy: the site is client-rendered, so
 * only consumers that execute JavaScript ever see these values — browser tabs,
 * history entries, and a minority of crawlers. Discord, Slack, X and Facebook
 * read the markup as served, which is why `index.html` carries a complete and
 * self-sufficient set of `og:` tags. These are the per-page refinement, never
 * the baseline.
 */
export interface PageMeta {
  title: string;
  description: string;
  /** Absolute URL of the social image for this page. */
  image: string;
  url: string;
}

/** Find (or create) a meta tag and set its content. */
function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

export function usePageMeta({ title, description, image, url }: PageMeta) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);

    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="og:url"]', "property", "og:url", url);

    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // Deliberately no cleanup: the next route overwrites every one of these,
    // and restoring the previous values on unmount would only make the tags
    // flicker back to the old page between renders.
  }, [title, description, image, url]);

  // The GA4 page view rides along here rather than in a router-level listener,
  // because this is the point at which the title for the new route exists. A
  // listener next to the router runs before the page's own effects, and would
  // report every route under the title of the one before it.
  const lastSent = useRef<string>("");
  useEffect(() => {
    // StrictMode invokes effects twice in development, and a route can
    // re-render for reasons that are not a navigation. One view per page.
    const view = `${url} :: ${title}`;
    if (lastSent.current === view) return;
    lastSent.current = view;

    trackPageView(title, url);
  }, [title, url]);
}
